import { ServiceBooking } from "@turizi/shared";
import { useState, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { Button, NumberInput, Popover, TextInput, Tooltip, Switch, Loader } from "@mantine/core";
import { MoneyIn } from "../snippets";
import { formatDayShort, formatPrice, formatExchangeRate } from "@/lib/format-utils";
import CurrencySelector from "../currency-selector";
import { IconRefreshAlert, IconAlertCircle, IconCashEdit, IconHandClick, IconArrowUpRight, IconArrowDownRight, IconExchange } from "@tabler/icons-react";
import { useResetServiceBookingPrice, useUpdateServiceBookingMoneyIn, useUpdateServiceBookingModifiers } from "@/hooks/api-hooks";
import { localUser } from "@/services/session-service";

interface PriceSectionProps {
    booking: ServiceBooking | undefined;
    canEditManualPriceCost?: boolean;
}

export default function PriceSection({
    booking,
    canEditManualPriceCost,
}: PriceSectionProps) {
    const { t } = useTranslation();
    const currentUser = localUser();
    const canEdit = canEditManualPriceCost !== undefined
        ? canEditManualPriceCost
        : currentUser?.permissions?.edit_prices_and_costs_manually !== false;
    const {
        mutate: updateMoney,
        isPending: updatingMoney
    } = useUpdateServiceBookingMoneyIn();
    const {
        mutate: resetMoney,
        isPending: resettingMoney
    } = useResetServiceBookingPrice();
    const {
        mutate: updateModifiers,
        isPending: updatingModifiers
    } = useUpdateServiceBookingModifiers();
    const [amount, setAmount] = useState<number | undefined>();
    const [currency, setCurrency] = useState<string>(booking?.money_in_currency || '');
    const [description, setDescription] = useState<string>(booking?.manual_price_description || '');
    const [pricePopoverOpened, setPricePopoverOpened] = useState(false);

    const availableModifiers = useMemo(() => {
        const appliedMods = booking?.applied_money_mods || [];
        const templateMods = booking?.service_data?.amount_in_modifiers || [];

        const map = new Map<string, { id: string; name: string; amount: number; applied: boolean }>();
        for (const tm of templateMods) {
            map.set(tm.id, { id: tm.id, name: tm.name, amount: tm.amount, applied: false });
        }
        for (const am of appliedMods) {
            map.set(am.id, { id: am.id, name: am.name, amount: am.amount, applied: !!am.applied });
        }
        return Array.from(map.values());
    }, [booking?.applied_money_mods, booking?.service_data?.amount_in_modifiers]);

    const handleToggleModifier = (modId: string, currentApplied: boolean) => {
        if (!booking?.id) return;
        const newApplied = !currentApplied;
        const updatedMods = availableModifiers.map(m =>
            m.id === modId ? { ...m, applied: newApplied } : m
        );

        updateModifiers({
            id: booking.id,
            applied_money_mods: updatedMods,
        });
    };

    const origAmount = booking?.price_info?.money_in_amount;
    const origCurrency = booking?.price_info?.money_in_currency || '**';
    const currentAmount = booking?.money_in_amount;
    const currentCurrency = booking?.money_in_currency || '**';

    const isManual = Boolean(booking?.manual_price);

    const isSameCurrency = !origCurrency || !currentCurrency || origCurrency === currentCurrency;
    const isDifferentCurrency = Boolean(origCurrency && currentCurrency && origCurrency !== currentCurrency);

    let percentDiff: number | null = null;
    let exchangeRate: number | null = null;

    if (isManual && origAmount != null && currentAmount != null && origAmount > 0) {
        if (isSameCurrency) {
            percentDiff = ((currentAmount - origAmount) / origAmount) * 100;
        } else if (isDifferentCurrency) {
            exchangeRate = currentAmount / origAmount;
        }
    }

    const handleSave = () => {
        if (!booking?.id) return;
        updateMoney({
            id: booking.id,
            data: {
                money_in_amount: amount,
                money_in_currency: currency,
                manual_price: true,
                manual_price_description: description,
                reservation_id: booking?.reservation_id
            }
        }, {
            onSuccess: () => setPricePopoverOpened(false)
        });
    }

    const handleReset = () => {
        if (!booking?.id) return;
        resetMoney({ id: booking.id }, {
            onSuccess: () => {
                setPricePopoverOpened(false);
            }
        });
    }

    return (
        <div className="solid-box-border-tinted space-y-2">
            {/* Price Modifiers */}
            {availableModifiers.length > 0 && (
                <div className="space-y-1.5 pb-2 border-b border-gray-200/80">
                    <div className="space-y-1">
                        {availableModifiers.map((mod) => {
                            const isAddition = mod.amount >= 0;
                            return (
                                <div
                                    key={mod.id}
                                    className="flex items-center justify-between gap-2 p-1.5 rounded-md bg-white border border-gray-100 hover:border-gray-200 transition-colors"
                                >
                                    <div className="flex items-center gap-2 min-w-0">
                                        {
                                            updatingModifiers
                                                ?
                                                <Loader size="xs" color="black" />
                                                :
                                                <Switch
                                                    size="xs"
                                                    color="black"
                                                    checked={mod.applied}
                                                    disabled={updatingModifiers || Boolean(booking?.parent_booking_id)}
                                                    onChange={() => handleToggleModifier(mod.id, mod.applied)}
                                                />
                                        }
                                        <span className="text-xs text-gray-700 truncate font-medium">
                                            {mod.name}
                                        </span>
                                    </div>
                                    <span className={`text-xs font-bold shrink-0 ${isAddition ? 'text-green-700' : 'text-red-700'}`}>
                                        {isAddition ? '+' : ''}{formatPrice(mod.amount)} <span className="text-gray-400 font-normal">{booking?.money_in_currency}</span>
                                    </span>
                                </div>
                            );
                        })}
                    </div>
                </div>
            )}

            {!booking?.parent_booking_id && canEdit && (
                <Button
                    variant="subtle"
                    color="green"
                    radius="xl"
                    size="xs"
                    onClick={handleReset}
                    loading={resettingMoney}
                    disabled={updatingMoney || resettingMoney}
                    leftSection={<IconRefreshAlert size={16} stroke={2} />}
                    fullWidth
                >
                    {t('reset_price')}
                </Button>
            )}

            <div className="max-h-[300px] overflow-y-auto space-y-2">
                {/* Price Breakdown Details */}
                {booking?.price_info?.steps?.map((step: any, index: number) => {
                    return (
                        <div key={step.id || `step-${index}`} className="space-y-0.5">
                            {/* Step Header */}
                            <div className="text-xs font-bold text-gray-800 uppercase tracking-wide flex items-center justify-between">
                                <div>
                                    {t(step.label)}
                                </div>
                            </div>
                            {/* Sub-steps */}
                            {step?.subSteps && Array.isArray(step.subSteps) && step.subSteps.length > 0 && (
                                <div>
                                    {step.subSteps.map((subStep: any, subIndex: number) => (
                                        <div key={subStep.id || `substep-${index}-${subIndex}`} className="flex items-start justify-between gap-3 py-1">
                                            <div className="flex-1 min-w-0">
                                                <div className="text-xs text-gray-700">
                                                    {t(subStep.label)}
                                                </div>
                                                {subStep.calculation && (
                                                    <div className="text-xxs text-gray-400 ">
                                                        {subStep.calculation}
                                                    </div>
                                                )}
                                            </div>
                                            <div className="text-xs font-semibold text-gray-900 whitespace-nowrap">
                                                {subStep.negative ? '-' : (step.label === 'applied_modifiers' ? '+' : '')}{formatPrice(subStep.amount)} <span className="text-gray-400 font-normal">{booking?.price_info?.money_in_currency}</span>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                    );
                }) || null}
            </div>

            {/* Final Price - Prominent */}
            {
                booking?.parent_booking_id ? (
                    <Tooltip label={t('child_booking_price_tooltip')}>
                        <div className="w-full border border-gray-200 bg-gray-50/50 p-2.5 rounded-lg flex items-center justify-between select-none">
                            <div className="text-xxs text-gray-400 font-semibold italic flex items-center gap-1">
                                <IconAlertCircle size={12} />
                                {t('included_in_package')}
                            </div>
                            <div>
                                <MoneyIn
                                    className="text-xl font-bold text-gray-900"
                                    amount={booking?.money_in_amount}
                                    currency={booking?.money_in_currency}
                                />
                            </div>
                        </div>
                    </Tooltip>
                ) : !canEdit ? (
                    <div className="w-full solid-box-border flex items-center gap-6 justify-between select-none">
                        <div />
                        <div>
                            <div className="flex gap-1 items-center justify-end">
                                <MoneyIn
                                    className="text-xl text-green-700"
                                    amount={booking?.money_in_amount}
                                    currency={booking?.money_in_currency}
                                />
                                {isManual ? (
                                    <IconHandClick size={12} color="gray" />
                                ) : null}
                            </div>
                            {booking?.price_info?.price_used && (
                                <div className="text-xs text-gray-400 text-right mt-1 flex items-center gap-1 justify-end">
                                    {formatDayShort(booking?.price_info?.price_used?.start_day)} - {formatDayShort(booking?.price_info?.price_used?.end_day)}
                                </div>
                            )}
                        </div>
                    </div>
                ) : (
                    <Popover
                        withArrow
                        shadow="xl"
                        radius="md"
                        trapFocus={false}
                        opened={pricePopoverOpened}
                    >
                        <Popover.Target>
                            <div
                                className="w-full solid-box-border-hover flex items-center gap-6 justify-between cursor-pointer group"
                                onClick={() => setPricePopoverOpened(!pricePopoverOpened)}
                            >
                                <div className="flex items-center gap-1 text-xs text-gray-400 group-hover:text-blue-600 transition-colors">
                                    <IconCashEdit size={14} />
                                </div>
                                <div>
                                    <div className="flex gap-1 items-center justify-end">
                                        <MoneyIn
                                            className="text-xl text-green-700"
                                            amount={booking?.money_in_amount}
                                            currency={booking?.money_in_currency}
                                        />
                                        {isManual ? (
                                            <IconHandClick size={12} color="gray" />
                                        ) : null}
                                    </div>
                                    {booking?.price_info?.price_used && (
                                        <div className="text-xs text-gray-400 text-right mt-1 flex items-center gap-1 justify-end">
                                            {formatDayShort(booking?.price_info?.price_used?.start_day)} - {formatDayShort(booking?.price_info?.price_used?.end_day)}
                                        </div>
                                    )}
                                </div>
                            </div>
                        </Popover.Target>
                        <Popover.Dropdown>
                            <div className="space-y-3 p-1">
                                <div className="text-sm font-semibold text-gray-900 mb-2">
                                    {t('update_price_manually')}
                                </div>
                                <div className="mt-2 space-y-1">
                                    <NumberInput
                                        autoFocus
                                        placeholder={t('amount')}
                                        leftSection={"$"}
                                        decimalSeparator="," // TODO internationalization
                                        thousandSeparator="." // TODO internationalization
                                        hideControls
                                        value={amount === undefined ? '' : amount}
                                        onChange={(value) => setAmount(value === '' ? undefined : Number(value))}
                                    />
                                    <CurrencySelector
                                        label={null}
                                        value={currency}
                                        onChange={(value) => setCurrency(value)}
                                        placeholder={t('currency')}
                                        clearable={false}
                                    />
                                    <TextInput
                                        placeholder={t('manual_price_reason_placeholder')}
                                        value={description}
                                        onChange={(event) => setDescription(event.currentTarget.value)}
                                        size="sm"
                                    />
                                </div>

                                <div className="flex justify-end gap-2 mt-4">
                                    <Button
                                        color="dark"
                                        size="xs"
                                        radius="xl"
                                        loading={updatingMoney}
                                        disabled={updatingMoney || resettingMoney || !currency}
                                        onClick={handleSave}
                                    >
                                        {t('save')}
                                    </Button>
                                </div>
                            </div>
                        </Popover.Dropdown>
                    </Popover>
                )
            }

            {/* Original Price & Adjustment Info (if manual / modified) */}
            {isManual && (
                <div className="space-y-1">
                    <div className="flex flex-wrap items-center justify-between gap-2">
                        <div className="flex justify-end items-center gap-1.5 w-full">
                            <span className="text-xs font-medium text-slate-400 whitespace-nowrap">
                                {t('original_price')}:
                            </span>
                            <MoneyIn
                                className="text-xs font-bold text-slate-700"
                                amount={origAmount}
                                currency={origCurrency}
                            />
                        </div>

                        {/* Percentage up/down if same currency */}
                        {isSameCurrency && percentDiff != null && (
                            <div
                                className={`inline-flex items-center gap-0.5 px-2 py-0.5 rounded-full text-xs font-bold whitespace-nowrap border ${percentDiff > 0
                                    ? 'bg-emerald-50 text-emerald-800 border-emerald-200'
                                    : percentDiff < 0
                                        ? 'bg-amber-50 text-amber-800 border-amber-200'
                                        : 'bg-slate-100 text-slate-700 border-slate-200'
                                    }`}
                            >
                                {percentDiff > 0 ? (
                                    <IconArrowUpRight size={14} />
                                ) : percentDiff < 0 ? (
                                    <IconArrowDownRight size={14} />
                                ) : null}
                                <span>
                                    {percentDiff > 0
                                        ? `+${percentDiff.toFixed(1).replace(/\.0$/, '')}%`
                                        : `${percentDiff.toFixed(1).replace(/\.0$/, '')}%`}
                                </span>
                            </div>
                        )}

                        {/* Exchange rate if different currency */}
                        {isDifferentCurrency && exchangeRate != null && (
                            <div className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg bg-blue-50/80 text-blue-900 border border-blue-200/80 text-xs font-semibold whitespace-nowrap">
                                <IconExchange size={14} className="text-blue-600 shrink-0" />
                                <span>
                                    1 {origCurrency} = {formatExchangeRate(exchangeRate)} {currentCurrency}
                                </span>
                            </div>
                        )}
                    </div>

                    {booking?.manual_price_description && (
                        <div className="text-xs text-slate-500 italic border-t border-slate-200/60 pt-1 mt-1">
                            {booking.manual_price_description}
                        </div>
                    )}
                </div>
            )}
        </div>
    );
}